import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Gift, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => Number(n || 0).toLocaleString('id-ID');

export default function LoyaltyRewards() {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    image_url: '',
    points_required: 100,
    reward_type: 'custom',
    reward_product_id: '',
    reward_voucher_code: '',
    stock: 0,
    is_active: true,
  });

  const fetchRewards = async () => {
    try {
      const r = await api.get('/tenant/loyalty-rewards');
      setRewards(r.data.rewards || []);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRewards(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: '', description: '', image_url: '', points_required: 100,
      reward_type: 'custom', reward_product_id: '', reward_voucher_code: '',
      stock: 0, is_active: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || '',
      description: r.description || '',
      image_url: r.image_url || '',
      points_required: r.points_required || 100,
      reward_type: r.reward_type || 'custom',
      reward_product_id: r.reward_product_id || '',
      reward_voucher_code: r.reward_voucher_code || '',
      stock: r.stock || 0,
      is_active: r.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Nama reward harus diisi'); return; }
    if (form.points_required <= 0) { toast.error('Poin harus lebih dari 0'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/tenant/loyalty-rewards/${editing.id}`, form);
        toast.success('Reward berhasil diupdate');
      } else {
        await api.post('/tenant/loyalty-rewards', form);
        toast.success('Reward berhasil dibuat');
      }
      setDialogOpen(false);
      fetchRewards();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus reward ini?')) return;
    try {
      await api.delete(`/tenant/loyalty-rewards/${id}`);
      toast.success('Reward dihapus');
      fetchRewards();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menghapus');
    }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Gift size={24} /> Katalog Reward</h1>
          <p className="text-slate-500 text-sm mt-1">Kelola reward yang bisa ditukar dengan poin</p>
        </div>
        <Button onClick={openCreate}><Plus size={16} className="mr-1" /> Tambah Reward</Button>
      </div>

      {rewards.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <Gift size={48} className="mx-auto mb-4 opacity-30" />
            <p>Belum ada reward. Buat reward pertama untuk katalog loyalty.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rewards.map(r => (
            <Card key={r.id} className={!r.is_active ? 'opacity-60' : ''}>
              <CardContent className="pt-4">
                <div className="flex gap-3">
                  {r.image_url ? (
                    <img src={r.image_url} alt={r.name} className="w-16 h-16 rounded object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                      <Gift size={24} className="text-amber-600" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{r.name}</h3>
                    <p className="text-sm text-slate-500 truncate">{r.description || '-'}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs">
                      <span className="font-medium text-amber-600">{fmt(r.points_required)} poin</span>
                      <span className="text-slate-400">|</span>
                      <span className="capitalize">{r.reward_type}</span>
                      <span className="text-slate-400">|</span>
                      <span>Stok: {r.stock === 0 ? '∞' : r.stock}</span>
                      <span className="text-slate-400">|</span>
                      <span>Diklaim: {r.claim_count || 0}x</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(r)}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(r.id)}><Trash2 size={14} /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Reward' : 'Tambah Reward'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nama Reward *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Contoh: Free Template Desain" />
            </div>
            <div>
              <Label>Deskripsi</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} />
            </div>
            <div>
              <Label>URL Gambar</Label>
              <Input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} placeholder="https://..." />
            </div>
            <div>
              <Label>Poin yang Dibutuhkan *</Label>
              <Input type="number" value={form.points_required} onChange={e => setForm(f => ({ ...f, points_required: parseInt(e.target.value) || 0 }))} min={1} />
            </div>
            <div>
              <Label>Tipe Reward</Label>
              <Select value={form.reward_type} onValueChange={v => setForm(f => ({ ...f, reward_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom / Hadiah Fisik</SelectItem>
                  <SelectItem value="voucher">Kode Voucher</SelectItem>
                  <SelectItem value="product">Produk Gratis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.reward_type === 'voucher' && (
              <div>
                <Label>Kode Voucher</Label>
                <Input value={form.reward_voucher_code} onChange={e => setForm(f => ({ ...f, reward_voucher_code: e.target.value }))} placeholder="REWARD50" className="uppercase" />
              </div>
            )}
            {form.reward_type === 'product' && (
              <div>
                <Label>Product ID</Label>
                <Input value={form.reward_product_id} onChange={e => setForm(f => ({ ...f, reward_product_id: e.target.value }))} placeholder="ID produk" />
              </div>
            )}
            <div>
              <Label>Stok (0 = unlimited)</Label>
              <Input type="number" value={form.stock} onChange={e => setForm(f => ({ ...f, stock: parseInt(e.target.value) || 0 }))} min={0} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} />
              <Label>Aktif</Label>
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              {editing ? 'Update Reward' : 'Buat Reward'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
